import { css } from "@emotion/react";
import { NoSsr } from "@mui/base";
import { theme } from "@product/scmp-sdk";
import { useSetAtom } from "jotai";
import { useRouter } from "next/router";
import type { FunctionComponent } from "react";
import { Fragment } from "react";

import { HamburgerMenu } from "~/components/posties/hamburger-menu";
import { hamburgerMenuAtom } from "~/components/posties/hamburger-menu/atoms";
import {
  Item as HamburgerMenuItem,
  SubItem as HamburgerMenuSubItem,
  StyledIconHome,
  StyledIconStar,
} from "~/components/posties/hamburger-menu/styles";
import { postiesMenuData as brochureHomepageMenuData } from "~/components/posties/header/posties-brochure-header/header-menu/data";
import { useMenuData as useContentHomepageMenuData } from "~/components/posties/header/posties-content-header/header-menu/hooks";
import { sendGATracking } from "~/components/tracking/google-analytics/apis";
import type {
  PostiesMenuActionButtonLabel,
  PostiesMenuItemName,
  PostiesSubMenuItemName,
} from "~/components/tracking/google-analytics/types";
import { PageType } from "~/lib/router/enums";
import { useCurrentPageType } from "~/lib/router/hooks";
import { pathname as contentHomepagePathname, page as postiesKidsPage } from "~/pages/posties/kids";
import { page as parentsPage } from "~/pages/posties/parents";

import { DynamicHeaderAvatar } from "./dynamics";
import {
  ActionButton,
  Container,
  FeaturesWrapper,
  RedirectButtonContainer,
  StyledButton,
} from "./styles";

export type Props = {
  className?: string;
};

export const HeaderActions: FunctionComponent<Props> = ({ className }) => {
  const setHamburgerMenuState = useSetAtom(hamburgerMenuAtom);

  const pageType = useCurrentPageType();
  const { asPath } = useRouter();
  const { menuData: contentHomepageMenuData } = useContentHomepageMenuData();
  const { pathname, query } = postiesKidsPage.route({});

  const label: PostiesMenuActionButtonLabel =
    pageType === PageType.PostiesParents ? "For Kids" : "For Parents";

  const renderButton = (onClick?: () => void) =>
    pageType === PageType.PostiesParents ? (
      <StyledButton pathname={pathname} query={query} variant="link" onClick={onClick}>
        {label}
      </StyledButton>
    ) : (
      <StyledButton {...parentsPage.route({})} variant="link" onClick={onClick}>
        {label}
      </StyledButton>
    );

  const handleRedirectButtonClick = () => {
    sendGATracking({
      action: "Header/Click",
      category: "Header",
      label,
    });
  };

  const renderHamburgerMenu = () => {
    const handleClose = () => {
      setHamburgerMenuState(current => ({
        ...current,
        isOpen: false,
      }));
    };

    const getHamburgerMenuItemClickHandler = (itemName: string) => () => {
      /** remove the trailing question mark */
      const label = itemName.replace(/\?$/, "") as PostiesMenuItemName | PostiesSubMenuItemName;
      handleClose();
      sendGATracking({
        action: "Menu/Click",
        category: "Menu",
        label,
      });
    };

    const handleHamburgerMenuRedirectButtonClick = () => {
      handleClose();
      sendGATracking({
        action: "Menu/Click",
        category: "Menu",
        label,
      });
    };

    const renderMenuItems = () =>
      pageType === PageType.PostiesParents
        ? brochureHomepageMenuData.map((item, index) => (
            <HamburgerMenuItem
              $isActive={item.urlAlias === asPath}
              key={index}
              pathname={item.urlAlias}
              onClick={getHamburgerMenuItemClickHandler(item.name)}
            >
              <StyledIconStar />
              {item.name}
            </HamburgerMenuItem>
          ))
        : contentHomepageMenuData.map((item, itemIndex) => (
            <Fragment key={itemIndex}>
              <HamburgerMenuItem
                $isActive={item.urlAlias === asPath}
                pathname={item.urlAlias ?? ""}
                onClick={getHamburgerMenuItemClickHandler(item.name)}
              >
                <StyledIconStar />
                {item.urlAlias === contentHomepagePathname && <StyledIconHome />}
                {item.name}
              </HamburgerMenuItem>
              {item.subMenuLinkProps?.map((subItem, subItemIndex) => (
                <HamburgerMenuSubItem
                  $isActive={subItem.urlAlias === asPath}
                  key={subItemIndex}
                  pathname={subItem.urlAlias}
                  onClick={getHamburgerMenuItemClickHandler(subItem.name)}
                >
                  <StyledIconStar />
                  {subItem.name}
                </HamburgerMenuSubItem>
              ))}
            </Fragment>
          ));
    return (
      <HamburgerMenu>
        <NoSsr>
          {renderMenuItems()}
          {renderButton(handleHamburgerMenuRedirectButtonClick)}
        </NoSsr>
      </HamburgerMenu>
    );
  };

  return (
    <Container className={className}>
      <FeaturesWrapper>
        <ActionButton
          $responsiveVariants={{
            desktopUp: "show",
            largeDesktopUp: "show",
            mobileUp: "hidden",
            tabletUp: "show",
          }}
        >
          <RedirectButtonContainer>
            {renderButton(handleRedirectButtonClick)}
          </RedirectButtonContainer>
        </ActionButton>
        <ActionButton
          $responsiveVariants={{
            desktopUp: "show",
            largeDesktopUp: "show",
            mobileUp: "show",
            tabletUp: "show",
          }}
        >
          <DynamicHeaderAvatar />
        </ActionButton>
        <ActionButton
          $extraCss={css`
            ${theme.breakpoints.up("tablet")} {
              margin-inline-start: 8px;
            }
          `}
          $responsiveVariants={{
            desktopUp: "hidden",
            largeDesktopUp: "hidden",
            mobileUp: "show",
            tabletUp: "show",
          }}
        >
          {renderHamburgerMenu()}
        </ActionButton>
      </FeaturesWrapper>
    </Container>
  );
};

HeaderActions.displayName = "HeaderActions";
